import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Gift, ArrowRight, Sparkles, Users, ShieldCheck, Check,
  Copy, MessageCircle, Mail, HelpCircle,
} from "lucide-react";
import Layout from "../components/Layout";

const REFERRAL_BONUS = 50;

/**
 * ReferralProgram — public-facing page explaining the Velcor referral
 * program. Reached by clicking the homepage `ReferralBanner` CTA.
 * Mirrors the structure of the in-portal `/portal/refer` (simulator + 3
 * trust columns + FAQ) but oriented at prospects (no signed-in code or
 * personal stats). The single CTA goal: send the visitor to /address so
 * they activate their own service and start earning.
 */
export default function ReferralProgram() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";

  return (
    <Layout ribbon>
      <Helmet>
        <title>
          {lang === "en"
            ? `Refer a friend, earn $${REFERRAL_BONUS} — Velcor Energy`
            : `Refiere a un amigo, gana $${REFERRAL_BONUS} — Velcor Energy`}
        </title>
        <meta
          name="description"
          content={
            lang === "en"
              ? `Earn $${REFERRAL_BONUS} in bill credit for every friend who activates Velcor Energy with your code. No referral cap.`
              : `Gana $${REFERRAL_BONUS} de crédito por cada amigo que active Velcor Energy con tu código. Sin límite de referidos.`
          }
        />
      </Helmet>

      <main data-testid="referral-program-page">
        {/* ===== HERO ===== */}
        <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/40 via-white to-white">
          <div className="max-w-[1180px] mx-auto px-5 py-16 md:py-24 grid md:grid-cols-[1.05fr_1fr] gap-10 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/10 px-3 py-1.5 text-velcor-green mb-5"
                style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.16em" }}>
                <Gift className="w-3 h-3" strokeWidth={2.4} />
                {lang === "en" ? "REFERRAL PROGRAM" : "PROGRAMA DE REFERIDOS"}
              </span>

              <h1
                className="display text-velcor-ink leading-[1.05] tracking-tight"
                style={{ fontSize: "clamp(34px, 5.6vw, 60px)", fontWeight: 600, letterSpacing: "-0.025em" }}
                data-testid="referral-program-title"
              >
                {lang === "en" ? (
                  <>Refer a friend.<br /><span className="text-velcor-green">Get ${REFERRAL_BONUS}.</span></>
                ) : (
                  <>Refiere a un amigo.<br /><span className="text-velcor-green">Gana ${REFERRAL_BONUS}.</span></>
                )}
              </h1>

              <p className="mt-5 text-slate-600 leading-relaxed max-w-[480px] text-[15px] md:text-[16px]">
                {lang === "en"
                  ? <>For every friend who activates Velcor Energy with your referral code, <strong className="text-velcor-ink">you receive ${REFERRAL_BONUS} of bill credit</strong> applied to your next invoice. No cap, no expiration, no paperwork.</>
                  : <>Por cada amigo que active Velcor Energy con tu código, <strong className="text-velcor-ink">recibes ${REFERRAL_BONUS} de crédito</strong> aplicados a tu próxima factura. Sin límite, sin vencimiento, sin papeleos.</>}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/address"
                  className="inline-flex items-center gap-2 rounded-full bg-velcor-green text-white px-6 py-3.5 hover:bg-velcor-green/95 active:scale-[0.98] transition-all duration-200 shadow-[0_18px_36px_rgba(47,122,58,0.28)] font-extrabold text-[14.5px]"
                  data-testid="referral-program-cta-activate"
                >
                  {lang === "en" ? "Activate my service" : "Activar mi servicio"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/portal/login"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-velcor-green/40 bg-white text-velcor-green px-6 py-3.5 hover:border-velcor-green hover:bg-velcor-green/5 transition-all font-extrabold text-[14.5px]"
                  data-testid="referral-program-cta-login"
                >
                  {lang === "en" ? "I'm already a customer" : "Ya soy cliente"}
                </Link>
              </div>

              <div className="mt-7 inline-flex items-center gap-2 text-[12px] text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                {lang === "en" ? "Credit posts after 30 days of active service." : "El crédito se aplica tras 30 días de servicio activo."}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="relative"
            >
              <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] md:min-h-[480px] shadow-[0_28px_60px_rgba(15,23,42,0.12)]">
                <img
                  src="/referral-share.png"
                  alt={lang === "en" ? "Friends sharing Velcor" : "Amigos compartiendo Velcor"}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 ring-1 ring-black/5 rounded-[28px] pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== 3 STEPS ===== */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-[1080px] mx-auto px-5">
            <div className="text-center mb-12">
              <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2">
                {lang === "en" ? "HOW IT WORKS" : "CÓMO FUNCIONA"}
              </div>
              <h2 className="display text-velcor-ink"
                style={{ fontSize: "clamp(26px, 3.4vw, 38px)", fontWeight: 600, letterSpacing: "-0.02em" }}>
                {lang === "en" ? "Three steps to earn" : "Tres pasos para ganar"}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {(lang === "en" ? [
                { n: "01", t: "Share your code", b: "Send your unique link via WhatsApp, SMS, email or copy it from your customer portal." },
                { n: "02", t: "Your friend activates", b: "They enter your code at velcorenergy.com when applying for their plan. Activation is free." },
                { n: "03", t: `You earn $${REFERRAL_BONUS}`, b: "Once your referral has 30 days of active service, $${REFERRAL_BONUS} of credit posts to your next bill." },
              ] : [
                { n: "01", t: "Comparte tu código", b: "Envía tu enlace único por WhatsApp, SMS, correo, o cópialo desde tu portal." },
                { n: "02", t: "Tu amigo activa", b: "Ingresa tu código en velcorenergy.com al solicitar su plan. Activación gratis." },
                { n: "03", t: `Tú ganas $${REFERRAL_BONUS}`, b: "Cuando tu referido cumpla 30 días de servicio activo, $${REFERRAL_BONUS} de crédito se aplican a tu próxima factura." },
              ]).map((s) => (
                <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-7 hover:border-velcor-green/40 hover:shadow-[0_22px_60px_-30px_rgba(15,29,61,0.18)] transition-all">
                  <div className="text-velcor-green/30 leading-none mb-4 tabular-nums"
                    style={{ fontSize: 56, fontWeight: 800, letterSpacing: "-0.04em" }}>
                    {s.n}
                  </div>
                  <h3 className="font-extrabold text-velcor-ink text-[18px] mb-2 leading-tight">{s.t}</h3>
                  <p className="text-slate-500 text-[14px] leading-relaxed">{s.b.replace(/\$\$\{REFERRAL_BONUS\}/g, "$" + REFERRAL_BONUS)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== EARNINGS SIMULATOR ===== */}
        <EarningsSimulator lang={lang} />

        {/* ===== WHY VELCOR ===== */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-[1080px] mx-auto px-5">
            <div className="text-center mb-12">
              <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2">
                {lang === "en" ? "WHY YOUR FRIENDS WILL LOVE IT" : "POR QUÉ A TUS AMIGOS LES ENCANTARÁ"}
              </div>
              <h2 className="display text-velcor-ink"
                style={{ fontSize: "clamp(26px, 3.4vw, 38px)", fontWeight: 600, letterSpacing: "-0.02em" }}>
                {lang === "en" ? "Velcor isn't just cheaper. It's clearer." : "Velcor no es solo más barato. Es más claro."}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {(lang === "en" ? [
                { icon: Sparkles, t: "Fixed transparent rate", b: "No hidden fees, no surprise charges. The rate they sign with is the rate they keep." },
                { icon: Users, t: "Bilingual human support", b: "Real people in English or Spanish. WhatsApp, phone or email — 7 days a week." },
                { icon: Gift, t: "Cancel anytime", b: "No penalty, no deposit, no termination fee. They can cancel any time." },
              ] : [
                { icon: Sparkles, t: "Tarifa fija y transparente", b: "Sin cargos ocultos, sin sorpresas. La tarifa con la que firma es la que mantiene." },
                { icon: Users, t: "Soporte humano bilingüe", b: "Personas reales en inglés o español. WhatsApp, teléfono o correo — 7 días a la semana." },
                { icon: Gift, t: "Cancela cuando quieras", b: "Sin penalidad, sin depósito, sin cargo de terminación. Puede cancelar en cualquier momento." },
              ]).map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-6">
                    <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 mb-4">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </span>
                    <h3 className="font-extrabold text-velcor-ink text-[15.5px] mb-2 leading-tight">{b.t}</h3>
                    <p className="text-slate-500 text-[13.5px] leading-relaxed">{b.b}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="bg-slate-50/60 py-16 md:py-20">
          <div className="max-w-[820px] mx-auto px-5">
            <div className="flex items-center gap-2 mb-8">
              <HelpCircle className="w-5 h-5 text-emerald-600" />
              <h2 className="display text-velcor-ink"
                style={{ fontSize: "clamp(22px, 2.8vw, 32px)", fontWeight: 600, letterSpacing: "-0.02em" }}>
                {lang === "en" ? "Frequently asked questions" : "Preguntas frecuentes"}
              </h2>
            </div>

            <div className="space-y-3">
              {(lang === "en" ? [
                { q: "How many people can I refer?", a: "No cap. You earn $50 for every referral that completes 30 days of active service." },
                { q: "When does the credit show up?", a: "It applies automatically to your next bill issued after your referral's 30-day mark." },
                { q: "Does my friend pay anything extra?", a: "No. They just enter your code at sign-up and pay the same rate as everyone else." },
                { q: "Can I get cash instead of credit?", a: "No, credits are only redeemable against your Velcor electricity bill." },
                { q: "Do I need to be a customer to refer?", a: `Yes — referral credits post to your Velcor account. Activate service first, then start earning $${REFERRAL_BONUS} per referral.` },
                { q: "What if my friend cancels early?", a: "If they cancel before completing 30 days of active service, the credit doesn't apply." },
              ] : [
                { q: "¿A cuántas personas puedo referir?", a: "Sin límite. Ganas $50 por cada referido que complete 30 días de servicio activo." },
                { q: "¿Cuándo aparece el crédito?", a: "Se aplica automáticamente a tu próxima factura emitida tras los 30 días de tu referido." },
                { q: "¿Mi amigo paga algo extra?", a: "No. Solo ingresa tu código al registrarse y paga la misma tarifa que cualquier cliente." },
                { q: "¿Puedo recibirlo en efectivo?", a: "No, los créditos solo se aplican a tu factura de electricidad Velcor." },
                { q: "¿Necesito ser cliente para referir?", a: `Sí — los créditos se aplican a tu cuenta Velcor. Activa servicio primero y empieza a ganar $${REFERRAL_BONUS} por referido.` },
                { q: "¿Y si mi amigo cancela antes?", a: "Si cancela antes de cumplir 30 días de servicio activo, el crédito no aplica." },
              ]).map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} testid={`referral-program-faq-${i}`} />
              ))}
            </div>
          </div>
        </section>

        {/* ===== BOTTOM CTA ===== */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-[820px] mx-auto px-5">
            <div className="rounded-3xl bg-gradient-to-br from-velcor-green via-emerald-700 to-velcor-green text-white p-8 md:p-12 text-center relative overflow-hidden">
              <div aria-hidden className="absolute inset-0 opacity-20"
                style={{ background: "radial-gradient(600px 320px at 50% 0%, rgba(255,255,255,0.4), transparent 60%)" }} />
              <div className="relative">
                <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-emerald-100 mb-3">
                  {lang === "en" ? "READY TO START EARNING?" : "¿LISTO PARA EMPEZAR A GANAR?"}
                </div>
                <h2 className="font-extrabold leading-tight tracking-tight mb-3"
                  style={{ fontSize: "clamp(26px, 3.4vw, 36px)" }}>
                  {lang === "en"
                    ? `Activate Velcor. Refer friends. Get $${REFERRAL_BONUS} every time.`
                    : `Activa Velcor. Refiere amigos. Gana $${REFERRAL_BONUS} cada vez.`}
                </h2>
                <p className="text-emerald-50 text-[14.5px] max-w-[520px] mx-auto mb-7 leading-relaxed">
                  {lang === "en"
                    ? "Most customers activate in under 1 hour with no deposit. Your referral link lives in your customer portal — share it once, earn forever."
                    : "La mayoría activan en menos de 1 hora sin depósito. Tu enlace de referido vive en tu portal — compártelo una vez, gana para siempre."}
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link
                    to="/address"
                    className="inline-flex items-center gap-2 rounded-full bg-white text-velcor-green px-7 py-3.5 font-extrabold text-[14.5px] hover:bg-emerald-50 transition-all"
                    data-testid="referral-program-bottom-cta"
                  >
                    {lang === "en" ? "Activate my service" : "Activar mi servicio"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}

/* ---------- Earnings simulator (public version, no logged-in data) ---------- */
function EarningsSimulator({ lang }) {
  const [count, setCount] = useState(5);
  const yearly = count * REFERRAL_BONUS;
  return (
    <section className="bg-gradient-to-b from-slate-50/60 to-white py-16 md:py-20" data-testid="referral-program-simulator">
      <div className="max-w-[820px] mx-auto px-5">
        <div className="text-center mb-10">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2">
            {lang === "en" ? "EARNINGS SIMULATOR" : "SIMULADOR DE GANANCIAS"}
          </div>
          <h2 className="display text-velcor-ink"
            style={{ fontSize: "clamp(24px, 3.2vw, 34px)", fontWeight: 600, letterSpacing: "-0.02em" }}>
            {lang === "en" ? "How much could you earn this year?" : "¿Cuánto podrías ganar este año?"}
          </h2>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">
              {lang === "en" ? "Friends you can refer" : "Amigos que puedes referir"}
            </label>
            <input
              type="range"
              min={1}
              max={20}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10))}
              className="w-full accent-velcor-green"
              data-testid="referral-program-simulator-slider"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1.5 tabular-nums">
              <span>1</span><span>5</span><span>10</span><span>15</span><span>20</span>
            </div>
            <div className="text-[14px] text-slate-600 mt-3">
              <strong className="text-velcor-ink tabular-nums">{count}</strong> {lang === "en" ? `referral${count !== 1 ? "s" : ""}` : `referido${count !== 1 ? "s" : ""}`} {lang === "en" ? "× $50" : "× $50"}
            </div>
          </div>
          <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 px-7 py-5 text-center min-w-[180px]">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-emerald-700 mb-2">
              {lang === "en" ? "Your earnings" : "Tus ganancias"}
            </div>
            <div className="text-emerald-700 leading-none tabular-nums"
              style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.025em" }}>
              ${yearly}
            </div>
            <div className="text-[11px] text-emerald-700/80 mt-2">
              {lang === "en" ? "in bill credits per year" : "en créditos al año"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ Item ---------- */
function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50/50 transition-colors"
        data-testid={testid}
      >
        <span className="text-[14.5px] font-bold text-velcor-ink">{q}</span>
        <span className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 -mt-1 text-[13.5px] text-slate-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
